import React, { useState, useEffect } from 'react';
import { Row, Col, Statistic, Empty, Progress, DatePicker, Select, Button, message, Switch } from 'antd';
import HeartLoader from '../../../components/shared/HeartLoader';
import {
  HeartOutlined,
  ExperimentOutlined,
  MedicineBoxOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { usePageHeader } from '../../../layouts/AdminLayout';
import {
  StyledAnalyticsTabs,
  TabsAndFiltersRow,
  FiltersContainer,
  ChartCard,
  StatCard,
  StatsRow,
  SectionTitle,
  ChartsRow,
  PageContainer,
  selectStyles,
  rangePickerStyles,
  valueColors,
  suffixStyles,
  iconColors
} from '../styled';
import { CategoryLabel, marginStyles } from './styled';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const HealthOutcomes = ({ dateRange: propDateRange, timeRange: propTimeRange }) => {
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [dateRange, setDateRange] = useState(propDateRange || [dayjs().subtract(30, 'days'), dayjs()]);
  const [quickRange, setQuickRange] = useState(propTimeRange || '30days');
  
  // Data states
  const [summaryData, setSummaryData] = useState(null);
  const [bpData, setBpData] = useState(null);
  const [cholesterolData, setCholesterolData] = useState(null);
  const [glucoseData, setGlucoseData] = useState(null);
  const [lpaData, setLpaData] = useState(null);
  const [excludeArchived, setExcludeArchived] = useState(true);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD')
      };
      const response = await api.getBlob('/screening/exports/screening-data', params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `screening-data-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Screening data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export screening data');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setPageHeader({ title: 'Health Outcomes Analytics', count: null, countIcon: null });
  }, [setPageHeader]);

  useEffect(() => {
    loadData();
  }, [dateRange, excludeArchived]);

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      setQuickRange('custom');
    }
  };

  const handleQuickRangeChange = (value) => {
    setQuickRange(value);
    const now = dayjs();
    let newRange;
    switch (value) {
      case '7days': newRange = [now.subtract(7, 'days'), now]; break;
      case '14days': newRange = [now.subtract(14, 'days'), now]; break;
      case '30days': newRange = [now.subtract(30, 'days'), now]; break;
      case '3months': newRange = [now.subtract(3, 'months'), now]; break;
      case 'Q1': newRange = [dayjs().startOf('year'), dayjs().startOf('year').month(2).endOf('month')]; break;
      case 'Q2': newRange = [dayjs().startOf('year').month(3), dayjs().startOf('year').month(5).endOf('month')]; break;
      case 'Q3': newRange = [dayjs().startOf('year').month(6), dayjs().startOf('year').month(8).endOf('month')]; break;
      case 'Q4': newRange = [dayjs().startOf('year').month(9), dayjs().startOf('year').month(11).endOf('month')]; break;
      default: return;
    }
    setDateRange(newRange);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        timeRange: quickRange,
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        ...(excludeArchived && { excludeArchived: 'true' })
      };

      // Load all data in parallel
      const [summaryRes, bpRes, cholesterolRes, glucoseRes, lpaRes] = await Promise.all([
        api.get('/screening/analytics/health-outcomes/summary', params),
        api.get('/screening/analytics/health-outcomes/bp-distribution', params),
        api.get('/screening/analytics/health-outcomes/cholesterol', params),
        api.get('/screening/analytics/health-outcomes/glucose', params),
        api.get('/screening/analytics/health-outcomes/lpa', params)
      ]);

      // Handle both response formats: { success, data } or direct data object
      const extractData = (res) => res.data?.success !== undefined 
        ? (res.data.success ? res.data.data : null) 
        : res.data;

      const summary = extractData(summaryRes);
      const bp = extractData(bpRes);
      const cholesterol = extractData(cholesterolRes);
      const glucose = extractData(glucoseRes);
      const lpa = extractData(lpaRes);

      if (summary) setSummaryData(summary);
      if (bp) setBpData(bp);
      if (cholesterol) setCholesterolData(cholesterol);
      if (glucose) setGlucoseData(glucose);
      if (lpa) setLpaData(lpa);
    } catch (error) {
      console.error('Error loading health outcomes data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => {
    if (!summaryData) {
      return <Empty description="No data available" />;
    }

    // BP Distribution Donut Chart
    const bpLabels = bpData?.distribution?.map(item => item.label) || [];
    const bpSeries = bpData?.distribution?.map(item => item.count) || [];
    const bpColors = bpData?.distribution?.map(item => item.color) || ['#108043', '#faad14', '#fa8c16', '#b91c1c', '#7f1d1d'];

    const bpPieOptions = {
      chart: { type: 'donut' },
      labels: bpLabels,
      colors: bpColors,
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const label = opts.w.globals.labels[opts.seriesIndex];
          return `${label}: ${val.toFixed(1)}%`;
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '50%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => bpSeries.reduce((a, b) => a + b, 0)
              }
            }
          }
        }
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} readings`
        }
      }
    };

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Blood Pressure Readings"
                value={summaryData.bloodPressure?.readings || 0}
                prefix={<HeartOutlined />}
                suffix={
                  <span style={{ fontSize: 14, color: (summaryData.bloodPressure?.abnormalRate || 0) > 30 ? '#b91c1c' : '#108043' }}>
                    {summaryData.bloodPressure?.abnormalRate || 0}% abnormal
                  </span>
                }
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Cholesterol Tests"
                value={summaryData.cholesterol?.tests || 0}
                prefix={<ExperimentOutlined />}
                suffix={
                  <span style={{ fontSize: 14, color: (summaryData.cholesterol?.highRate || 0) > 30 ? '#b91c1c' : '#108043' }}>
                    {summaryData.cholesterol?.highRate || 0}% high
                  </span>
                }
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Glucose/A1C Tests"
                value={summaryData.glucose?.tests || 0}
                prefix={<MedicineBoxOutlined />}
                suffix={
                  <span style={{ fontSize: 14, color: (summaryData.glucose?.atRiskRate || 0) > 30 ? '#b91c1c' : '#108043' }}>
                    {summaryData.glucose?.atRiskRate || 0}% at risk
                  </span>
                }
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Lp(a) Tests"
                value={summaryData.lpa?.tests || 0}
                prefix={<AlertOutlined />}
                suffix={
                  <span style={{ fontSize: 14, color: (summaryData.lpa?.elevatedRate || 0) > 20 ? '#b91c1c' : '#108043' }}>
                    {summaryData.lpa?.elevatedRate || 0}% elevated
                  </span>
                }
              />
            </StatCard>
          </Col>
        </StatsRow>

        {/* Quick BP Distribution Chart */}
        {bpData && bpData.distribution && (
          <ChartCard title="Blood Pressure Distribution">
            <Row gutter={24}>
              <Col xs={24} lg={12}>
                <Chart
                  options={bpPieOptions}
                  series={bpSeries}
                  type="donut"
                  height={300}
                />
              </Col>
              <Col xs={24} lg={12}>
                <SectionTitle><HeartOutlined /> Category Breakdown</SectionTitle>
                {bpData.distribution.map((cat, idx) => (
                  <div key={idx} style={marginStyles.mb16}>
                    <CategoryLabel>
                      <span className="label">{cat.label} ({cat.description})</span>
                      <span className="value">{cat.count} ({cat.percentage}%)</span>
                    </CategoryLabel>
                    <Progress
                      percent={parseFloat(cat.percentage)}
                      strokeColor={cat.color}
                      showInfo={false}
                    />
                  </div>
                ))}
              </Col>
            </Row>
          </ChartCard>
        )}
      </>
    );
  };

  const renderBloodPressure = () => {
    if (!bpData) {
      return <Empty description="No blood pressure data available" />;
    }

    // BP Distribution Donut
    const bpDistLabels = bpData.distribution?.map(item => item.label) || [];
    const bpDistSeries = bpData.distribution?.map(item => item.count) || [];
    const bpDistColors = bpData.distribution?.map(item => item.color) || ['#108043', '#faad14', '#fa8c16', '#b91c1c', '#7f1d1d'];

    const bpDistOptions = {
      chart: { type: 'donut' },
      labels: bpDistLabels,
      colors: bpDistColors,
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const label = opts.w.globals.labels[opts.seriesIndex];
          return `${label}: ${val.toFixed(1)}%`;
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '50%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => bpDistSeries.reduce((a, b) => a + b, 0)
              }
            }
          }
        }
      }
    };

    // BP by Age Group Stacked Bar
    const ageData = Object.entries(bpData.byAge || {}).map(([age, data]) => ({
      age,
      ...data
    }));
    const ageCategories = ageData.map(item => item.age);
    
    const ageBarSeries = [
      { name: 'Normal', data: ageData.map(item => item.normal || 0) },
      { name: 'Elevated', data: ageData.map(item => item.elevated || 0) },
      { name: 'Stage 1', data: ageData.map(item => item.stage1 || 0) },
      { name: 'Stage 2', data: ageData.map(item => item.stage2 || 0) },
      { name: 'Crisis', data: ageData.map(item => item.crisis || 0) }
    ];

    const ageBarOptions = {
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: { show: false }
      },
      colors: ['#108043', '#faad14', '#fa8c16', '#b91c1c', '#7f1d1d'],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '60%'
        }
      },
      xaxis: { categories: ageCategories },
      yaxis: { title: { text: 'Count' } },
      legend: { position: 'top' },
      dataLabels: { enabled: false }
    };

    // Abnormal Rate Trend Line
    const trendData = bpData.trends || [];
    const trendCategories = trendData.map(item => item.month);
    const trendSeries = [{
      name: 'Abnormal Rate',
      data: trendData.map(item => item.abnormalRate || 0)
    }];

    const trendOptions = {
      chart: {
        type: 'line',
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      colors: ['#b91c1c'],
      stroke: { curve: 'smooth', width: 3 },
      markers: { size: 5 },
      xaxis: { categories: trendCategories },
      yaxis: {
        title: { text: 'Abnormal Rate (%)' },
        labels: { formatter: (val) => `${val}%` }
      },
      tooltip: {
        y: { formatter: (val) => `${val}%` }
      }
    };

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Total Readings"
                value={bpData.summary?.totalReadings || 0}
                prefix={<HeartOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Normal Readings"
                value={bpData.summary?.normalReadings || 0}
                valueStyle={{ color: valueColors.success }}
                prefix={<CheckCircleOutlined />}
                suffix={<span style={suffixStyles}>{bpData.summary?.normalRate || 0}%</span>}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Abnormal Readings"
                value={bpData.summary?.abnormalReadings || 0}
                valueStyle={{ color: valueColors.warning }}
                prefix={<WarningOutlined />}
                suffix={<span style={suffixStyles}>{bpData.summary?.abnormalRate || 0}%</span>}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Crisis Readings"
                value={bpData.summary?.crisisReadings || 0}
                valueStyle={{ color: valueColors.danger }}
                prefix={<AlertOutlined />}
                suffix={<span style={suffixStyles}>{bpData.summary?.crisisRate || 0}%</span>}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="BP Category Distribution">
              {bpData.distribution && bpData.distribution.length > 0 ? (
                <Chart
                  options={bpDistOptions}
                  series={bpDistSeries}
                  type="donut"
                  height={350}
                />
              ) : (
                <Empty description="No distribution data" />
              )}
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="BP by Age Group">
              {ageData.length > 0 ? (
                <Chart
                  options={ageBarOptions}
                  series={ageBarSeries}
                  type="bar"
                  height={350}
                />
              ) : (
                <Empty description="No age group data" />
              )}
            </ChartCard>
          </Col>
        </ChartsRow>

        {trendData.length > 0 && (
          <ChartCard title="Abnormal Rate Trend Over Time">
            <Chart
              options={trendOptions}
              series={trendSeries}
              type="line"
              height={300}
            />
          </ChartCard>
        )}
      </>
    );
  };

  const renderCholesterol = () => {
    if (!cholesterolData) {
      return <Empty description="No cholesterol data available" />;
    }

    // Total Cholesterol Donut
    const totalCholLabels = cholesterolData.totalCholesterol?.map(item => item.label) || [];
    const totalCholSeries = cholesterolData.totalCholesterol?.map(item => item.count) || [];
    const totalCholColors = cholesterolData.totalCholesterol?.map(item => item.color) || ['#108043', '#faad14', '#b91c1c'];

    const totalCholOptions = {
      chart: { type: 'donut' },
      labels: totalCholLabels,
      colors: totalCholColors,
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`
      },
      plotOptions: {
        pie: {
          donut: {
            size: '45%'
          }
        }
      }
    };

    // LDL Cholesterol Donut
    const ldlLabels = cholesterolData.ldl?.map(item => item.label) || [];
    const ldlSeries = cholesterolData.ldl?.map(item => item.count) || [];
    const ldlColors = cholesterolData.ldl?.map(item => item.color) || ['#108043', '#1890ff', '#faad14', '#fa8c16', '#b91c1c'];

    const ldlOptions = {
      chart: { type: 'donut' },
      labels: ldlLabels,
      colors: ldlColors,
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`
      },
      plotOptions: {
        pie: {
          donut: {
            size: '45%'
          }
        }
      }
    };

    // HDL Cholesterol Donut
    const hdlLabels = cholesterolData.hdl?.map(item => item.label) || [];
    const hdlSeries = cholesterolData.hdl?.map(item => item.count) || [];
    const hdlColors = cholesterolData.hdl?.map(item => item.color) || ['#b91c1c', '#faad14', '#108043'];

    const hdlOptions = {
      chart: { type: 'donut' },
      labels: hdlLabels,
      colors: hdlColors,
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`
      },
      plotOptions: {
        pie: {
          donut: {
            size: '45%'
          }
        }
      }
    };

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Total Tests"
                value={cholesterolData.summary?.totalTests || 0}
                prefix={<ExperimentOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Patients Tested"
                value={cholesterolData.summary?.patientsWithData || 0}
                suffix={<span style={suffixStyles}>of {cholesterolData.summary?.totalPatients || 0}</span>}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Coverage Rate"
                value={cholesterolData.summary?.coverageRate || 0}
                suffix="%"
                valueStyle={{ color: (cholesterolData.summary?.coverageRate || 0) > 50 ? '#108043' : '#faad14' }}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={8}>
            <ChartCard title="Total Cholesterol">
              {cholesterolData.totalCholesterol && cholesterolData.totalCholesterol.length > 0 ? (
                <>
                  <Chart
                    options={totalCholOptions}
                    series={totalCholSeries}
                    type="donut"
                    height={280}
                  />
                  <div style={marginStyles.mt16}>
                    {cholesterolData.totalCholesterol.map((cat, idx) => (
                      <CategoryLabel key={idx}>
                        <span className="label">{cat.label} ({cat.range})</span>
                        <span className="value">{cat.count} ({cat.percentage}%)</span>
                      </CategoryLabel>
                    ))}
                  </div>
                </>
              ) : (
                <Empty description="No total cholesterol data" />
              )}
            </ChartCard>
          </Col>
          <Col xs={24} lg={8}>
            <ChartCard title="LDL Cholesterol">
              {cholesterolData.ldl && cholesterolData.ldl.length > 0 ? (
                <Chart
                  options={ldlOptions}
                  series={ldlSeries}
                  type="donut"
                  height={300}
                />
              ) : (
                <Empty description="No LDL data" />
              )}
            </ChartCard>
          </Col>
          <Col xs={24} lg={8}>
            <ChartCard title="HDL Cholesterol">
              {cholesterolData.hdl && cholesterolData.hdl.length > 0 ? (
                <Chart
                  options={hdlOptions}
                  series={hdlSeries}
                  type="donut"
                  height={300}
                />
              ) : (
                <Empty description="No HDL data" />
              )}
            </ChartCard>
          </Col>
        </ChartsRow>
      </>
    );
  };

  const renderGlucose = () => {
    if (!glucoseData) {
      return <Empty description="No glucose data available" />;
    }

    // A1C Distribution Donut
    const a1cLabels = glucoseData.a1c?.map(item => item.label) || [];
    const a1cSeries = glucoseData.a1c?.map(item => item.count) || [];
    const a1cColors = glucoseData.a1c?.map(item => item.color) || ['#108043', '#faad14', '#b91c1c'];

    const a1cOptions = {
      chart: { type: 'donut' },
      labels: a1cLabels,
      colors: a1cColors,
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const label = opts.w.globals.labels[opts.seriesIndex];
          return `${label}: ${val.toFixed(1)}%`;
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '50%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => a1cSeries.reduce((a, b) => a + b, 0)
              }
            }
          }
        }
      }
    };

    // Blood Glucose Distribution Donut
    const glucoseLabels = glucoseData.glucose?.map(item => item.label) || [];
    const glucoseSeries = glucoseData.glucose?.map(item => item.count) || [];
    const glucoseColors = glucoseData.glucose?.map(item => item.color) || ['#108043', '#faad14', '#b91c1c'];

    const glucoseChartOptions = {
      chart: { type: 'donut' },
      labels: glucoseLabels,
      colors: glucoseColors,
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const label = opts.w.globals.labels[opts.seriesIndex];
          return `${label}: ${val.toFixed(1)}%`;
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '50%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => glucoseSeries.reduce((a, b) => a + b, 0)
              }
            }
          }
        }
      }
    };

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="A1C Tests"
                value={glucoseData.summary?.a1cTests || 0}
                prefix={<MedicineBoxOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Glucose Tests"
                value={glucoseData.summary?.glucoseTests || 0}
                prefix={<ExperimentOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="A1C Coverage"
                value={glucoseData.summary?.a1cCoverage || 0}
                suffix="%"
                valueStyle={{ color: (glucoseData.summary?.a1cCoverage || 0) > 50 ? '#108043' : '#faad14' }}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Glucose Coverage"
                value={glucoseData.summary?.glucoseCoverage || 0}
                suffix="%"
                valueStyle={{ color: (glucoseData.summary?.glucoseCoverage || 0) > 50 ? '#108043' : '#faad14' }}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="A1C Distribution">
              {glucoseData.a1c && glucoseData.a1c.length > 0 ? (
                <>
                  <Chart
                    options={a1cOptions}
                    series={a1cSeries}
                    type="donut"
                    height={300}
                  />
                  <div style={marginStyles.mt16}>
                    {glucoseData.a1c.map((cat, idx) => (
                      <CategoryLabel key={idx}>
                        <span className="label">{cat.label} ({cat.range})</span>
                        <span className="value">{cat.count} ({cat.percentage}%)</span>
                      </CategoryLabel>
                    ))}
                  </div>
                </>
              ) : (
                <Empty description="No A1C data" />
              )}
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Blood Glucose Distribution">
              {glucoseData.glucose && glucoseData.glucose.length > 0 ? (
                <>
                  <Chart
                    options={glucoseChartOptions}
                    series={glucoseSeries}
                    type="donut"
                    height={300}
                  />
                  <div style={marginStyles.mt16}>
                    {glucoseData.glucose.map((cat, idx) => (
                      <CategoryLabel key={idx}>
                        <span className="label">{cat.label} ({cat.range})</span>
                        <span className="value">{cat.count} ({cat.percentage}%)</span>
                      </CategoryLabel>
                    ))}
                  </div>
                </>
              ) : (
                <Empty description="No glucose data" />
              )}
            </ChartCard>
          </Col>
        </ChartsRow>
      </>
    );
  };

  const renderLpa = () => {
    if (!lpaData) {
      return <Empty description="No Lp(a) data available" />;
    }

    // Lp(a) Distribution Donut
    const lpaLabels = lpaData.lpa?.map(item => item.label) || [];
    const lpaSeries = lpaData.lpa?.map(item => item.count) || [];
    const lpaColors = lpaData.lpa?.map(item => item.color) || ['#108043', '#faad14', '#b91c1c'];

    const lpaOptions = {
      chart: { type: 'donut' },
      labels: lpaLabels,
      colors: lpaColors,
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const label = opts.w.globals.labels[opts.seriesIndex];
          return `${label}: ${val.toFixed(1)}%`;
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '50%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => lpaSeries.reduce((a, b) => a + b, 0)
              }
            }
          }
        }
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} tests`
        }
      }
    };

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Total Lp(a) Tests"
                value={lpaData.summary?.totalTests || 0}
                prefix={<ExperimentOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Patients Tested"
                value={lpaData.summary?.patientsWithData || 0}
                suffix={<span style={suffixStyles}>of {lpaData.summary?.totalPatients || 0}</span>}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Coverage Rate"
                value={lpaData.summary?.coverageRate || 0}
                suffix="%"
                valueStyle={{ color: (lpaData.summary?.coverageRate || 0) > 50 ? '#108043' : '#faad14' }}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="High Risk (≥50 mg/dL)"
                value={lpaData.lpa?.find(c => c.category === 'high')?.count || 0}
                valueStyle={{ color: valueColors.danger }}
                prefix={<AlertOutlined />}
                suffix={<span style={suffixStyles}>{lpaData.lpa?.find(c => c.category === 'high')?.percentage || 0}%</span>}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Lp(a) Risk Distribution (NLA 2024 Guidelines)">
              {lpaData.lpa && lpaData.lpa.length > 0 ? (
                <Chart
                  options={lpaOptions}
                  series={lpaSeries}
                  type="donut"
                  height={350}
                />
              ) : (
                <Empty description="No Lp(a) data" />
              )}
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Lp(a) Category Breakdown">
              {lpaData.lpa && lpaData.lpa.length > 0 ? (
                <div style={marginStyles.mt16}>
                  {lpaData.lpa.map((cat, idx) => (
                    <div key={idx} style={marginStyles.mb16}>
                      <CategoryLabel>
                        <span className="label">{cat.label} ({cat.range})</span>
                        <span className="value">{cat.count} ({cat.percentage}%)</span>
                      </CategoryLabel>
                      <Progress
                        percent={parseFloat(cat.percentage)}
                        strokeColor={cat.color}
                        showInfo={false}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Empty description="No category data" />
              )}
            </ChartCard>
          </Col>
        </ChartsRow>
      </>
    );
  };

  const subTabItems = [
    { key: 'overview', label: 'Overview' },
    { key: 'blood-pressure', label: 'Blood Pressure' },
    { key: 'cholesterol', label: 'Cholesterol' },
    { key: 'glucose', label: 'Glucose / A1C' },
    { key: 'lpa', label: 'Lp(a)' }
  ];

  const renderContent = () => {
    switch (activeSubTab) {
      case 'blood-pressure':
        return renderBloodPressure();
      case 'cholesterol':
        return renderCholesterol();
      case 'glucose':
        return renderGlucose();
      case 'lpa':
        return renderLpa();
      default:
        return renderOverview();
    }
  };

  if (loading && !summaryData) {
    return (
      <PageContainer>
        <HeartLoader size="48px" fullHeight />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TabsAndFiltersRow className="filters-animate-in">
        <StyledAnalyticsTabs
          activeKey={activeSubTab}
          onChange={setActiveSubTab}
          items={subTabItems}
        />
        <FiltersContainer>
          <Select
            value={quickRange}
            onChange={handleQuickRangeChange}
            style={selectStyles}
            size="large"
          >
            <Option value="7days">Last 7 days</Option>
            <Option value="14days">Last 14 days</Option>
            <Option value="30days">Last 30 days</Option>
            <Option value="3months">Last 3 months</Option>
            <Option value="Q1">Q1 (Jan-Mar)</Option>
            <Option value="Q2">Q2 (Apr-Jun)</Option>
            <Option value="Q3">Q3 (Jul-Sep)</Option>
            <Option value="Q4">Q4 (Oct-Dec)</Option>
            <Option value="custom">Custom</Option>
          </Select>

          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="MM/DD/YY"
            style={rangePickerStyles}
            size="large"
            allowClear={false}
          />

          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
            size="large"
          >
            Export
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch size="small" checked={excludeArchived} onChange={setExcludeArchived} />
            <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Exclude Archived</span>
          </div>
        </FiltersContainer>
      </TabsAndFiltersRow>

      {loading ? (
        <HeartLoader size="32px" padding="40px" />
      ) : (
        <div className="content-animate-in">
          {renderContent()}
        </div>
      )}
    </PageContainer>
  );
};

export default HealthOutcomes;
